/**
 * Web credential helpers for Nostr secret keys.
 *
 * - Chromium: PasswordCredential API ("Save password?" prompt)
 * - Other browsers: silent no-op / file download fallback
 */

import { downloadTextFile } from '@/lib/downloadFile';

/** Whether the browser supports PasswordCredential (Chromium-only). */
export function supportsPasswordCredential(): boolean {
  return typeof window !== 'undefined' && 'PasswordCredential' in window;
}

/**
 * Store a Nostr secret key in the browser password manager (Chromium).
 * No-op on unsupported platforms.
 */
export async function storeNsecCredential(
  npub: string,
  nsec: string,
  name?: string,
): Promise<boolean> {
  if (!supportsPasswordCredential()) return false;

  try {
    const credential = new PasswordCredential({
      id: npub,
      password: nsec,
      name: name ?? npub,
    });
    await navigator.credentials.store(credential);
    return true;
  } catch {
    return false;
  }
}

/**
 * Retrieve a stored Nostr credential from the browser password manager.
 */
export async function getNsecCredential(): Promise<
  { npub: string; nsec: string } | undefined
> {
  if (!supportsPasswordCredential()) return undefined;

  try {
    const credential = await navigator.credentials.get({
      password: true,
      mediation: 'optional',
    } as CredentialRequestOptions);

    if (credential && 'password' in credential) {
      const pc = credential as PasswordCredential;
      if (pc.id && pc.password) {
        return { npub: pc.id, nsec: pc.password };
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export type SaveNsecResult = 'saved' | 'saved-to-file' | 'dismissed';

function nsecFilename(npub: string, appName?: string): string {
  const slug = (appName ?? location.hostname)
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'nostr';
  return `${slug}-${npub.slice(5, 9)}.nsec.txt`;
}

/**
 * Save an nsec: download as `.nsec.txt` and optionally store in the
 * browser password manager (Chromium).
 */
export async function saveNsec(
  npub: string,
  nsec: string,
  name?: string,
): Promise<SaveNsecResult> {
  await downloadTextFile(nsecFilename(npub, name), nsec);
  storeNsecCredential(npub, nsec, name).catch(() => {});
  return 'saved';
}
